import React, { useState, useMemo, useEffect } from 'react';
import { impressionsFor, isStreamContent, reachOf } from '../utils/reportMetrics';
import CampaignCreatorCard from './CampaignCreatorCard';
import apiClient from '../api/client';
import YouTubeEstimateTooltip from './YouTubeEstimateTooltip';
import PlatformIcon from './PlatformIcon';
import { isYouTubeDataStale } from '../utils/youtubeCompliance';
import EditContentMetricsModal from './EditContentMetricsModal';

// API base URL for thumbnail resolution
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper to resolve thumbnail URLs (local cached thumbnails start with /api/)
const resolveThumbnailUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
};

const CampaignContentView = ({ campaign, contents, creators, onContentClick, onRefresh }) => {
  const [viewMode, setViewMode] = useState('grouped');
  const [sortBy, setSortBy] = useState('contentCount');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterContentType, setFilterContentType] = useState('all');
  const [filterPayType, setFilterPayType] = useState('all'); // 'all' | 'organic' | 'sponsored'
  const [liveStreams, setLiveStreams] = useState([]);
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');

  const gameId = campaign?.game?.id;
  const gameName = campaign?.game?.title;

  // Fetch live streams for the campaign with smart polling
  useEffect(() => {
    if (!campaign?.id) return;

    let failureCount = 0;
    let timeoutId = null;
    let isVisible = !document.hidden;

    const fetchLiveStreams = async () => {
      // Skip polling when tab is hidden to save resources
      if (document.hidden) return;

      try {
        const response = await apiClient.get(`/campaigns/${campaign.id}/live`);
        console.log('Live streams fetched:', response.data);
        if (response.data.success && response.data.streams) {
          console.log('Setting live streams:', response.data.streams);
          setLiveStreams(response.data.streams);
        }
        failureCount = 0; // Reset on success
      } catch (error) {
        failureCount++;
        console.error('Failed to fetch live streams:', error);
        if (failureCount >= 5) {
          console.warn('Live stream polling stopped after 5 consecutive failures');
          return; // Stop polling after 5 failures
        }
      }

      // Exponential backoff: 30s, 60s, 120s, 240s (max)
      const baseInterval = 30000;
      const backoffMultiplier = Math.min(Math.pow(2, failureCount), 8);
      const nextInterval = baseInterval * backoffMultiplier;
      timeoutId = setTimeout(fetchLiveStreams, nextInterval);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && !isVisible) {
        // Tab became visible again - resume polling immediately
        isVisible = true;
        fetchLiveStreams();
      } else if (document.hidden) {
        isVisible = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    fetchLiveStreams();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [campaign?.id]);

  // Group contents by creator
  const groupedByCreator = useMemo(() => {
    // First, deduplicate contents to remove genuine duplicates. Use the same key as
    // the backend / campaign list: a bare channel URL (e.g. twitch.tv/afrodited13) is
    // shared by every one of a creator's streams, so never collapse on it — fall back
    // to the unique content id, or two distinct daily streams merge into one.
    const seen = new Set();
    let deduplicatedContents = contents.filter(content => {
      const nUrl = content.contentUrl
        ? content.contentUrl.toLowerCase().replace('://www.', '://').replace(/\/+$/, '')
        : null;
      let urlVideoId = null;
      if (nUrl) {
        const tw = nUrl.match(/\/videos\/(\d+)/);
        const tk = nUrl.match(/\/video\/(\d+)/);
        const yt = nUrl.match(/(?:[?&]v=|\/shorts\/|youtu\.be\/)([a-z0-9_-]+)/i);
        urlVideoId = (tw && `tw_${tw[1]}`) || (tk && `tk_${tk[1]}`) || (yt && `yt_${yt[1]}`) || null;
      }
      const isSpecificVodUrl = nUrl && /\/(videos|video|watch|v|shorts)\/[\w-]+/.test(nUrl);
      const key = content.vodId || urlVideoId || content.videoId || (isSpecificVodUrl ? nUrl : null) || content.id;
      if (seen.has(key)) {
        return false; // Skip duplicates
      }
      seen.add(key);
      return true;
    });

    // Filter by date range if specified
    if (filterDateStart || filterDateEnd) {
      deduplicatedContents = deduplicatedContents.filter(content => {
        const contentDate = new Date(content.postDate);
        if (filterDateStart) {
          const startDate = new Date(filterDateStart);
          startDate.setHours(0, 0, 0, 0);
          if (contentDate < startDate) return false;
        }
        if (filterDateEnd) {
          const endDate = new Date(filterDateEnd);
          endDate.setHours(23, 59, 59, 999);
          if (contentDate > endDate) return false;
        }
        return true;
      });
    }

    // Filter by paid type (Sponsored = isPaid, Organic = everything else)
    if (filterPayType !== 'all') {
      deduplicatedContents = deduplicatedContents.filter(content =>
        filterPayType === 'sponsored' ? !!content.isPaid : !content.isPaid
      );
    }

    const groups = {};

    deduplicatedContents.forEach(content => {
      const creatorId = content.creatorId;
      if (!groups[creatorId]) {
        groups[creatorId] = {
          creator: creators.find(c => c.id === creatorId) || content.creator || { id: creatorId, name: 'Unknown' },
          contents: []
        };
      }
      groups[creatorId].contents.push(content);
    });

    // Convert to array and sort
    let grouped = Object.values(groups);

    // Filter by platform - only show creators with content on that platform
    if (filterPlatform !== 'all') {
      grouped = grouped.map(g => ({
        ...g,
        contents: g.contents.filter(c => c.platform === filterPlatform)
      })).filter(g => g.contents.length > 0);
    }

    // Filter by content type (streams, videos, instagram)
    if (filterContentType !== 'all') {
      grouped = grouped.map(g => ({
        ...g,
        contents: g.contents.filter(c => {
          if (filterContentType === 'streams') {
            // Streams: Twitch VODs/streams and YouTube live streams
            return c.contentType === 'stream' || c.contentType === 'vod';
          } else if (filterContentType === 'youtube') {
            // YouTube videos and shorts (not live streams)
            return (c.platform === 'youtube') && c.contentType !== 'stream' && c.contentType !== 'vod';
          } else if (filterContentType === 'tiktok') {
            // TikTok content only
            return c.platform === 'tiktok';
          } else if (filterContentType === 'instagram') {
            // Instagram content only
            return c.platform === 'instagram';
          }
          return true;
        })
      })).filter(g => g.contents.length > 0);
    }

    // Sort
    switch (sortBy) {
      case 'contentCount':
        grouped.sort((a, b) => b.contents.length - a.contents.length);
        break;
      case 'views':
        grouped.sort((a, b) => {
          const aViews = a.contents.reduce((sum, c) => sum + (c.views || 0), 0);
          const bViews = b.contents.reduce((sum, c) => sum + (c.views || 0), 0);
          return bViews - aViews;
        });
        break;
      case 'recent':
        grouped.sort((a, b) => {
          const aRecent = Math.max(...a.contents.map(c => new Date(c.postDate || 0).getTime()));
          const bRecent = Math.max(...b.contents.map(c => new Date(c.postDate || 0).getTime()));
          return bRecent - aRecent;
        });
        break;
      default:
        break;
    }

    return grouped;
  }, [contents, creators, sortBy, filterPlatform, filterContentType, filterPayType, filterDateStart, filterDateEnd]);

  // Calculate totals - live stream CCV only, VOD views
  const totals = useMemo(() => {
    // Start with date-filtered content
    let filtered = contents;

    // Apply date range filter
    if (filterDateStart || filterDateEnd) {
      filtered = filtered.filter(content => {
        const contentDate = new Date(content.postDate);
        if (filterDateStart) {
          const startDate = new Date(filterDateStart);
          startDate.setHours(0, 0, 0, 0);
          if (contentDate < startDate) return false;
        }
        if (filterDateEnd) {
          const endDate = new Date(filterDateEnd);
          endDate.setHours(23, 59, 59, 999);
          if (contentDate > endDate) return false;
        }
        return true;
      });
    }

    // Apply platform filter
    if (filterPlatform !== 'all') {
      filtered = filtered.filter(c => c.platform === filterPlatform);
    }

    // Apply paid type filter (Sponsored = isPaid, Organic = everything else) so the
    // summary pills above reflect the selected tab too.
    if (filterPayType !== 'all') {
      filtered = filtered.filter(c => (filterPayType === 'sponsored' ? !!c.isPaid : !c.isPaid));
    }

    // Count unique creators directly instead of depending on groupedByCreator
    const uniqueCreatorIds = new Set(filtered.map(c => c.creatorId));
    const creatorCount = uniqueCreatorIds.size;

    const normalizedGameName = gameName?.toLowerCase().trim();
    let totalViews = 0;
    let youtubeViews = 0;
    let tiktokViews = 0;
    let instagramViews = 0;
    let totalVodViews = 0;
    let totalGameDuration = 0;
    let totalWatchHours = 0;

    // Track CCV from all streams (sum of all avg CCVs and sum of all peak CCVs)
    let streamCcvSum = 0;
    let streamPeakCCV = 0;
    let hasStreamContent = false; // Track if campaign has any stream/VOD content

    // Sum views and track game duration from all content
    const contentTypes = [...new Set(filtered.map(c => c.contentType))];
    console.log('[CampaignContentView] Content types found:', contentTypes);
    console.log('[CampaignContentView] Stream/VOD count:', filtered.filter(c => c.contentType === 'stream' || c.contentType === 'vod').length);
    filtered.forEach(content => {
      const contentTotalViews = content.views || 0;
      totalViews += contentTotalViews;

      // Track platform-specific views
      const platform = content.platform?.toLowerCase() || '';
      if (platform === 'youtube') {
        youtubeViews += contentTotalViews;
      } else if (platform === 'tiktok') {
        tiktokViews += contentTotalViews;
      } else if (platform === 'instagram') {
        instagramViews += contentTotalViews;
      }

      // Track VOD views across all platforms (Twitch + YouTube stream replays),
      // matching the client report's definition. Videos have null vodViews → contribute 0.
      totalVodViews += content.vodViews || 0;

      // Track game-specific duration from segments
      const gameSegments = content.segments?.filter(seg => {
        const segGameName = seg.gameName?.toLowerCase().trim();
        const segGameTitle = seg.game?.title?.toLowerCase().trim();

        return seg.gameId === gameId ||
               seg.game?.id === gameId ||
               segGameName === normalizedGameName ||
               segGameTitle === normalizedGameName;
      }) || [];

      if (gameSegments.length > 0 && (content.contentType === 'stream' || content.contentType === 'vod' || content.platform === 'twitch' || content.platform === 'kick')) {
        // Only count stream/VOD durations, not YouTube video durations
        totalGameDuration += content.streamDuration || gameSegments.reduce((sum, seg) => sum + (seg.durationMinutes || 0), 0);
      }

      // Track CCV metrics from completed streams (both Twitch and YouTube)
      if (content.contentType === 'stream' || content.contentType === 'vod') {
        hasStreamContent = true; // Campaign has stream content, show CCV headers
        // Check multiple sources for CCV: streamAvgCcv, avgCcv, or from segments
        let avgCcvValue = content.streamAvgCcv || content.avgCcv || 0;
        let peakCcvValue = content.streamPeakCcv || content.peakCcv || 0;

        // Also check segments for CCV data if not found on content
        if ((avgCcvValue === 0 || peakCcvValue === 0) && gameSegments.length > 0) {
          gameSegments.forEach(seg => {
            if (seg.avgCcv && seg.avgCcv > 0 && avgCcvValue === 0) {
              avgCcvValue = seg.avgCcv;
            }
            if (seg.peakCcv && seg.peakCcv > peakCcvValue) {
              peakCcvValue = seg.peakCcv;
            }
          });
        }

        // Sum up all CCV values across all content
        if (avgCcvValue > 0) {
          streamCcvSum += avgCcvValue;
        }
        if (peakCcvValue > 0) {
          streamPeakCCV += peakCcvValue;
        }
        if (content.streamWatchHours || content.watchHours) {
          totalWatchHours += content.streamWatchHours || content.watchHours || 0;
        }
      }
    });

    // Add live stream CCV to totals
    liveStreams.forEach(stream => {
      if (stream.viewerCount) {
        streamCcvSum += stream.viewerCount;
      }
      if (stream.peakViewerCount) {
        streamPeakCCV += stream.peakViewerCount;
      }
    });

    // Total CCV is the sum of all avg CCVs and sum of all peak CCVs
    const avgCCV = streamCcvSum;
    const peakCCV = streamPeakCCV;

    const hasCcvData = streamCcvSum > 0 || streamPeakCCV > 0;
    // Show CCV headers if there's stream content or CCV data
    const showCcvHeaders = hasStreamContent || hasCcvData || liveStreams.length > 0;

    console.log('[CampaignContentView] totals computed:', {
      hasStreamContent,
      hasCcvData,
      showCcvHeaders,
      liveStreamsCount: liveStreams.length,
      avgCCV,
      peakCCV,
      watchHours: totalWatchHours
    });

    // Summed PER ITEM: YouTube content posted on/after 2026-08-24 is estimated at
    // 5x rather than 10x, so multiplying the summed view total is wrong.
    const impressions = filtered.reduce(
      (sum, c) => sum + impressionsFor(c, isStreamContent(c) ? (c.vodViews || 0) : (c.views || 0)), 0);

    return {
      creators: creatorCount,
      contents: filtered.length,
      paidCount: filtered.filter(c => c.isPaid).length,
      views: totalViews,
      impressions,
      youtubeViews,
      tiktokViews,
      instagramViews,
      totalVodViews,
      duration: totalGameDuration,
      avgCCV,
      peakCCV,
      watchHours: totalWatchHours,
      hasLiveStreams: hasCcvData,
      showCcvHeaders,
      hasStreamContent
    };
  }, [contents, filterPlatform, filterPayType, filterDateStart, filterDateEnd, gameId, gameName, liveStreams]);

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div>
      {/* Summary Stats + Controls - Single Row */}
      {(() => {
        const P = ({ label, value, tooltip }) => (
          <span className="inline-flex items-center justify-center gap-1.5 rounded-full px-2.5 py-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-sc-pink whitespace-nowrap">
            <span className="text-[11px] text-white">{label}</span>
            {tooltip && <YouTubeEstimateTooltip metric={tooltip} />}
            <span className="text-[11px] font-semibold text-white">{value}</span>
          </span>
        );
        // Build paired columns - related metrics stacked
        const cols = [
          [
            { label: 'Creators', value: totals.creators },
            { label: 'Content', value: totals.contents },
          ],
          ...(totals.paidCount > 0 ? [[
            { label: 'Paid Content', value: totals.paidCount },
          ]] : []),
          [
            { label: 'Tot Stream Time', value: formatDuration(totals.duration) },
            ...(totals.showCcvHeaders ? [{ label: 'Avg CCV', value: totals.avgCCV > 0 ? formatNumber(totals.avgCCV) : 'N/A', tooltip: 'watchHours' }] : []),
          ],
          ...(totals.showCcvHeaders ? [[
            { label: 'Peak CCV', value: totals.peakCCV > 0 ? formatNumber(totals.peakCCV) : 'N/A' },
            { label: 'Watch Hours', value: totals.watchHours > 0 ? formatNumber(Math.round(totals.watchHours)) : 'N/A', tooltip: 'watchHours' },
          ]] : []),
          [
            { label: 'Combined Views', value: formatNumber(totals.views) },
            ...(totals.youtubeViews > 0 ? [{ label: 'YouTube Views', value: formatNumber(totals.youtubeViews), tooltip: 'views' }] : [{ label: 'Impressions', value: formatNumber(totals.impressions), tooltip: 'impressions' }]),
          ],
          ...(totals.youtubeViews > 0 ? [[
            ...(totals.totalVodViews > 0 ? [{ label: 'VoD Views', value: formatNumber(totals.totalVodViews) }] : []),
            { label: 'Impressions', value: formatNumber(totals.impressions), tooltip: 'impressions' },
          ]] : []),
          ...(totals.tiktokViews > 0 || totals.instagramViews > 0 ? [[
            ...(totals.tiktokViews > 0 ? [{ label: 'TikTok', value: formatNumber(totals.tiktokViews) }] : []),
            ...(totals.instagramViews > 0 ? [{ label: 'Instagram', value: formatNumber(totals.instagramViews) }] : []),
          ]] : []),
        ].filter(col => col.length > 0);

        return (
          <div className="flex items-start gap-2 mb-4 flex-wrap">
            {cols.map((col, i) => (
              <div key={i} className="flex flex-col gap-1 items-stretch">
                {col.map((pill, j) => <P key={j} label={pill.label} value={pill.value} tooltip={pill.tooltip} />)}
              </div>
            ))}

            <div className="w-px h-8 bg-white/10 mx-0.5 self-center" />

            <div className="flex flex-col gap-1 flex-shrink-0">
              <div className="flex items-center gap-1">
                {['all', 'streams', 'youtube', 'tiktok', 'instagram'].map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterContentType(type)}
                    className={`px-2.5 py-1 text-[11px] rounded-full transition-all ${
                      filterContentType === type
                        ? 'bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/40 text-white font-semibold'
                        : 'bg-white/5 border border-sc-pink text-white hover:text-white hover:border-sc-pink'
                    }`}
                  >
                    {type === 'all' ? 'All Content' : type === 'streams' ? 'Streams' : type === 'youtube' ? 'YouTube' : type === 'tiktok' ? 'TikTok' : 'Instagram'}
                  </button>
                ))}
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`px-2.5 py-1 text-[11px] rounded-full transition-all ${
                    viewMode === 'grouped'
                      ? 'bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/40 text-white font-semibold'
                      : 'bg-white/5 border border-sc-pink text-white hover:text-white hover:border-sc-pink'
                  }`}
                >
                  By Creator
                </button>
              </div>
              <div className="flex items-center gap-1">
                <input type="date" value={filterDateStart} onChange={(e) => setFilterDateStart(e.target.value)} className="px-2 py-1 border border-sc-pink rounded-full text-[11px] text-white w-[120px] [color-scheme:dark]" style={{ backgroundColor: 'rgba(168,85,247,0.12)' }} />
                <span className="text-white text-[11px]">→</span>
                <input type="date" value={filterDateEnd} onChange={(e) => setFilterDateEnd(e.target.value)} className="px-2 py-1 border border-sc-pink rounded-full text-[11px] text-white w-[120px] [color-scheme:dark]" style={{ backgroundColor: 'rgba(168,85,247,0.12)' }} />
                {(filterDateStart || filterDateEnd) && (
                  <button onClick={() => { setFilterDateStart(''); setFilterDateEnd(''); }} className="text-white hover:text-white text-xs">✕</button>
                )}
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-2.5 py-1 border border-sc-pink rounded-full text-[11px] text-white [color-scheme:dark]" style={{ backgroundColor: 'rgba(168,85,247,0.12)' }}>
                  <option value="contentCount">Most Content</option>
                  <option value="views">Most Views</option>
                  <option value="recent">Most Recent</option>
                </select>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Organic / Sponsored tabs — scope the content list AND the summary pills above */}
      <div className="flex items-center gap-1 mb-3">
        {[['all', 'All'], ['organic', 'Organic'], ['sponsored', 'Sponsored']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilterPayType(val)}
            className={`px-3 py-1 text-[11px] rounded-full transition-all ${
              filterPayType === val
                ? 'bg-gradient-to-r from-pink-500/30 to-purple-500/30 border border-pink-500/40 text-white font-semibold'
                : 'bg-white/5 border border-sc-pink text-white hover:border-sc-pink'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content Display */}
      {/* When a specific content type filter is active (not 'all'), show flat list */}
      {filterContentType !== 'all' ? (
        <FlatContentList
          contents={groupedByCreator.flatMap(g => g.contents)}
          creators={creators}
          gameName={gameName}
          gameId={gameId}
          onContentClick={onContentClick}
          contentType={filterContentType}
          onContentUpdated={onRefresh}
        />
      ) : viewMode === 'grouped' ? (
        <div className="space-y-2">
          {groupedByCreator.length > 0 ? (
            groupedByCreator.map(({ creator, contents }) => (
              <CampaignCreatorCard
                key={creator.id}
                creator={creator}
                contents={contents}
                gameName={gameName}
                gameId={gameId}
                campaignId={campaign?.id}
                onContentDeleted={onRefresh}
                onContentUpdated={onRefresh}
                onCreatorRemoved={onRefresh}
                onCreatorUpdated={onRefresh}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-[#1a1525]/80 rounded-lg border border-white">
              <p className="text-4xl mb-2">📭</p>
              <p className="text-white">No content found</p>
              <p className="text-sm text-white mt-1">Use the scanner to find content from your creators</p>
            </div>
          )}
        </div>
      ) : viewMode === 'timeline' ? (
        <TimelineView
          contents={contents.filter(c => filterPlatform === 'all' || c.platform === filterPlatform)}
          creators={creators}
          gameName={gameName}
          gameId={gameId}
          onContentClick={onContentClick}
        />
      ) : viewMode === 'grid' ? (
        <GridView
          contents={contents.filter(c => filterPlatform === 'all' || c.platform === filterPlatform)}
          creators={creators}
          gameName={gameName}
          gameId={gameId}
          onContentClick={onContentClick}
        />
      ) : null}
    </div>
  );
};

// Flat Content List - shows individual content pieces with creator info inline and full stats
const FlatContentList = ({ contents, creators, gameName, gameId, onContentClick, contentType, onContentUpdated }) => {
  const [sortField, setSortField] = React.useState('date');
  const [sortDirection, setSortDirection] = React.useState('desc');
  const [editingContent, setEditingContent] = React.useState(null);

  // Mark a piece of content Paid (with its own cost) or Organic. One creator can
  // have a paid stream and a paid video at different costs, plus organic posts.
  const handlePayment = async (e, content) => {
    e.stopPropagation();
    try {
      await apiClient.patch(`/content/${content.id}/payment`, { isPaid: !content.isPaid });
      if (onContentUpdated) onContentUpdated();
    } catch (err) { alert('Failed to update.'); }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedContents = React.useMemo(() => {
    return [...contents].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'date':
          aVal = new Date(a.postDate).getTime();
          bVal = new Date(b.postDate).getTime();
          break;
        case 'views':
          aVal = a.vodViews ?? a.views ?? 0;
          bVal = b.vodViews ?? b.views ?? 0;
          break;
        case 'likes':
          aVal = a.likes || 0;
          bVal = b.likes || 0;
          break;
        case 'comments':
          aVal = a.comments || 0;
          bVal = b.comments || 0;
          break;
        case 'engagement':
          aVal = a.views ? ((a.likes || 0) + (a.comments || 0)) / a.views : 0;
          bVal = b.views ? ((b.likes || 0) + (b.comments || 0)) / b.views : 0;
          break;
        case 'impressions':
          aVal = impressionsFor(a, reachOf(a));
          bVal = impressionsFor(b, reachOf(b));
          break;
        case 'avgCcv':
          aVal = a.streamAvgCcv || a.avgCcv || 0;
          bVal = b.streamAvgCcv || b.avgCcv || 0;
          break;
        case 'peakCcv':
          aVal = a.streamPeakCcv || a.peakCcv || 0;
          bVal = b.streamPeakCcv || b.peakCcv || 0;
          break;
        case 'watchHours':
          aVal = a.streamWatchHours || a.watchHours || 0;
          bVal = b.streamWatchHours || b.watchHours || 0;
          break;
        case 'duration':
          aVal = a.streamDuration || a.duration || 0;
          bVal = b.streamDuration || b.duration || 0;
          break;
        default:
          aVal = new Date(a.postDate).getTime();
          bVal = new Date(b.postDate).getTime();
      }

      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [contents, sortField, sortDirection]);

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getContentTypeLabel = () => {
    switch (contentType) {
      case 'streams': return 'Streams';
      case 'youtube': return 'YouTube Videos';
      case 'tiktok': return 'TikTok Videos';
      case 'instagram': return 'Instagram Posts';
      default: return 'Content';
    }
  };

  const getPlatformLabel = (platform) => {
    switch (platform) {
      case 'twitch': return 'Twitch';
      case 'kick': return 'Kick';
      case 'youtube': return 'YouTube';
      case 'tiktok': return 'TikTok';
      case 'instagram': return 'Instagram';
      default: return platform;
    }
  };

  // Get segment data for a specific content item (same logic as CampaignCreatorCard)
  const getGameSegmentData = (content) => {
    const normalizedGameName = gameName?.toLowerCase().trim();

    const gameSegments = content.segments?.filter(seg => {
      const segGameName = seg.gameName?.toLowerCase().trim();
      const segGameTitle = seg.game?.title?.toLowerCase().trim();

      if (segGameName === normalizedGameName || segGameTitle === normalizedGameName) {
        return true;
      }
      return seg.gameId === gameId || seg.game?.id === gameId;
    }) || [];

    if (gameSegments.length > 0) {
      // Prefer content.streamDuration if set (may be manually edited), otherwise use segment data
      const duration = content.streamDuration || gameSegments.reduce((sum, seg) => sum + (seg.durationMinutes || 0), 0);
      const avgCcv = Math.round(gameSegments.reduce((sum, seg) => sum + (seg.avgCcv || 0), 0) / gameSegments.length) || content.streamAvgCcv || content.avgCcv;
      const peakCcv = Math.max(...gameSegments.map(seg => seg.peakCcv || 0)) || content.streamPeakCcv || content.peakCcv;

      return { duration, avgCcv, peakCcv, hasSegments: true };
    }

    return {
      duration: content.streamDuration || content.duration || 0,
      avgCcv: content.streamAvgCcv || content.avgCcv,
      peakCcv: content.streamPeakCcv || content.peakCcv,
      hasSegments: false
    };
  };

  // Determine which stats columns to show based on content type
  const isStreamView = contentType === 'streams';
  const isVideoView = contentType === 'youtube' || contentType === 'tiktok';
  const isInstagramView = contentType === 'instagram';

  // Sortable header component
  const SortHeader = ({ field, label }) => (
    <button
      onClick={() => handleSort(field)}
      className={`text-xs font-medium whitespace-nowrap hover:text-sc-pink transition-colors flex items-center justify-end gap-0.5 w-full ${
        sortField === field ? 'text-sc-pink' : 'text-white'
      }`}
    >
      {label}
      {sortField === field && (
        <span className="text-[10px]">{sortDirection === 'desc' ? '▼' : '▲'}</span>
      )}
    </button>
  );

  if (sortedContents.length === 0) {
    return (
      <div className="text-center py-12 bg-[#1a1525]/80 rounded-lg border border-white">
        <p className="text-4xl mb-2">📭</p>
        <p className="text-white">No {getContentTypeLabel().toLowerCase()} found</p>
      </div>
    );
  }

  return (
    <div className="bg-[#241d33] rounded-lg border border-white overflow-hidden">
      {/* Header with sortable columns */}
      <div className="p-3 bg-white/5 border-b border-white/10 flex items-center gap-3">
        <div className="w-20 flex-shrink-0">
          <p className="font-medium text-white text-sm">{sortedContents.length} {getContentTypeLabel()}</p>
        </div>
        <div className="flex-1 min-w-0" />
        <div className="flex items-center gap-4 text-xs flex-shrink-0">
          <div className="w-16 text-right"><SortHeader field="date" label="Date" /></div>
          {isStreamView ? (
            <>
              <div className="w-16 text-right"><SortHeader field="duration" label="Duration" /></div>
              <div className="w-16 text-right"><SortHeader field="avgCcv" label="Avg CCV" /></div>
              <div className="w-16 text-right"><SortHeader field="peakCcv" label="Peak CCV" /></div>
              <div className="w-16 text-right"><SortHeader field="watchHours" label="Watch Hrs" /></div>
              <div className="w-20 text-right"><SortHeader field="views" label="VOD Views" /></div>
              <div className="w-20 text-right"><SortHeader field="impressions" label="Impressions" /></div>
            </>
          ) : (
            <>
              <div className="w-16 text-right"><SortHeader field="views" label="Views" /></div>
              <div className="w-20 text-right"><SortHeader field="impressions" label="Impressions" /></div>
              <div className="w-16 text-right"><SortHeader field="likes" label="Likes" /></div>
              <div className="w-20 text-right"><SortHeader field="comments" label="Comments" /></div>
              <div className="w-20 text-right"><SortHeader field="engagement" label="Engagement" /></div>
            </>
          )}
        </div>
        <div className="w-[52px] flex-shrink-0" />
      </div>
      <div className="divide-y divide-white/5">
        {sortedContents.map((content, idx) => {
          const creator = creators.find(c => c.id === content.creatorId) || content.creator;
          const segmentData = getGameSegmentData(content);

          // Calculate engagement for display
          const engagementRate = content.views && (content.likes || content.comments)
            ? (((content.likes || 0) + (content.comments || 0)) / content.views * 100).toFixed(2)
            : null;

          return (
            <div
              key={content.id || idx}
              className="p-3 hover:bg-white/5 cursor-pointer"
              onClick={() => {
                if (content.contentUrl) {
                  window.open(content.contentUrl, '_blank');
                }
                onContentClick?.(content);
              }}
            >
              <div className="flex items-center gap-3">
                {/* Thumbnail */}
                <div className="w-20 h-12 bg-white/10 rounded overflow-hidden flex-shrink-0 relative">
                  {(content.thumbnail || content.thumbnailUrl) ? (
                    <img
                      src={resolveThumbnailUrl(content.thumbnail || content.thumbnailUrl)}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xl">
                      <PlatformIcon platform={content.platform} size="xs" />
                    </div>
                  )}
                </div>

                {/* Content Info - Left side */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white text-sm truncate">{content.title || 'Untitled'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-white">{getPlatformLabel(content.platform)}</span>
                    {isYouTubeDataStale(content) && (
                      <span className="text-amber-500 text-xs" title="YouTube data >30 days old — refresh to update">&#9888;</span>
                    )}
                    <span className="text-white">•</span>
                    <div className="flex items-center gap-1">
                      {creator?.profileImageUrl ? (
                        <img
                          src={creator.profileImageUrl}
                          alt={creator.name}
                          className="w-4 h-4 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-sc-pink/20 flex items-center justify-center text-[8px] text-sc-pink font-bold">
                          {creator?.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                      <span className="text-xs text-white">{creator?.name || 'Unknown'}</span>
                    </div>
                  </div>
                </div>

                {/* Stats - Right side, aligned with header */}
                <div className="flex items-center gap-4 text-xs flex-shrink-0">
                  {/* Date */}
                  <span className="w-16 text-right text-white">{formatDate(content.postDate).replace(/, \d{4}$/, '')}</span>

                  {isStreamView ? (
                    // Stream stats columns
                    <>
                      <span className="w-16 text-right font-medium text-white">{formatDuration(segmentData.duration)}</span>
                      <span className="w-16 text-right font-medium text-white">{formatNumber(content.streamAvgCcv || content.avgCcv || segmentData.avgCcv || 0)}</span>
                      <span className="w-16 text-right font-medium text-white">{formatNumber(content.streamPeakCcv || content.peakCcv || segmentData.peakCcv || 0)}</span>
                      <span className="w-16 text-right font-medium text-white">{formatNumber(content.streamWatchHours || content.watchHours || 0)}</span>
                      <span className="w-20 text-right font-medium text-white">{formatNumber(content.vodViews ?? content.views ?? 0)}</span>
                      <span className="w-20 text-right font-medium text-white">{formatNumber(impressionsFor(content, reachOf(content)))}</span>
                    </>
                  ) : (
                    // Video/Social stats columns
                    <>
                      <span className="w-16 text-right font-medium text-white">{formatNumber(content.views || 0)}</span>
                      <span className="w-20 text-right font-medium text-white">{formatNumber(impressionsFor(content, reachOf(content)))}</span>
                      <span className="w-16 text-right font-medium text-white">{formatNumber(content.likes || 0)}</span>
                      <span className="w-20 text-right font-medium text-white">{formatNumber(content.comments || 0)}</span>
                      <span className="w-20 text-right font-medium text-white">{engagementRate ? `${engagementRate}%` : 'N/A'}</span>
                    </>
                  )}
                </div>

                {/* Paid / Organic + individual cost */}
                <button
                  onClick={(e) => handlePayment(e, content)}
                  className={`text-[11px] px-2 py-0.5 rounded-full flex-shrink-0 font-medium ${content.isPaid ? 'bg-sc-pink/20 text-sc-pink hover:bg-sc-pink/30' : 'bg-white/10 text-white hover:text-white'}`}
                  title="Mark paid / set cost"
                >
                  {content.isPaid ? 'Paid' : 'Organic'}
                </button>

                {/* Edit + Arrow */}
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingContent(content); }}
                  className="text-white hover:text-white text-xs flex-shrink-0 p-1 hover:bg-white/10 rounded"
                  title="Edit metrics"
                >
                  ✏️
                </button>
                <span className="text-white text-sm flex-shrink-0">→</span>
              </div>
            </div>
          );
        })}
      </div>

      <EditContentMetricsModal
        isOpen={!!editingContent}
        onClose={() => setEditingContent(null)}
        onSuccess={() => {
          setEditingContent(null);
          if (onContentUpdated) onContentUpdated();
        }}
        content={editingContent}
      />
    </div>
  );
};

// Timeline View Component
const TimelineView = ({ contents, creators, gameName, gameId, onContentClick }) => {
  const sortedContents = [...contents].sort((a, b) => 
    new Date(b.postDate) - new Date(a.postDate)
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatViews = (views) => {
    if (!views) return '0';
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  // Group by date
  const groupedByDate = sortedContents.reduce((acc, content) => {
    const date = formatDate(content.postDate);
    if (!acc[date]) acc[date] = [];
    acc[date].push(content);
    return acc;
  }, {});

  return (
    <div className="card-pink-accent bg-[#1a1525]/80 rounded-lg border border-white overflow-hidden">
      {Object.entries(groupedByDate).map(([date, dateContents]) => (
        <div key={date}>
          <div className="px-4 py-2 bg-white/5 border-b border-white/10 sticky top-0">
            <p className="font-medium text-white">{date}</p>
          </div>
          <div className="divide-y divide-white/5">
            {dateContents.map((content, idx) => {
              const creator = creators.find(c => c.id === content.creatorId) || content.creator;
              return (
                <div
                  key={content.id || idx}
                  className="p-4 hover:bg-white/5 cursor-pointer"
                  onClick={() => onContentClick?.(content)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-lg flex-shrink-0">
                      <PlatformIcon platform={content.platform} size="xs" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{content.title || 'Untitled'}</p>
                      <p className="text-sm text-white">{creator?.name || 'Unknown Creator'}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-white">
                      <span>👁️ {formatViews(content.views)}</span>
                      <span>⏱️ {formatDuration(content.streamDuration || content.duration)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Grid View Component
const GridView = ({ contents, creators, gameName, gameId, onContentClick }) => {
  const formatViews = (views) => {
    if (!views) return '0';
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}:${mins.toString().padStart(2, '0')}`;
    return `${mins}:00`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {contents.map((content, idx) => {
        const creator = creators.find(c => c.id === content.creatorId) || content.creator;
        return (
          <div
            key={content.id || idx}
            className="card-pink-accent bg-[#1a1525]/80 rounded-lg border border-white overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onContentClick?.(content)}
          >
            {/* Thumbnail */}
            <div className="relative aspect-video bg-white/10">
              {(content.thumbnail || content.thumbnailUrl) ? (
                <img
                  src={resolveThumbnailUrl(content.thumbnail || content.thumbnailUrl)}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl">
                  <PlatformIcon platform={content.platform} size="xs" />
                </div>
              )}
              {/* Duration Badge */}
              {(content.streamDuration || content.duration) && (
                <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 text-white text-xs rounded">
                  {formatDuration(content.streamDuration || content.duration)}
                </span>
              )}
              {/* Platform Badge */}
              <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs font-medium rounded ${
                content.platform === 'twitch' ? 'bg-purple-600 text-white' :
                content.platform === 'kick' ? 'bg-[#00e70a] text-black' :
                content.platform === 'youtube' ? 'bg-red-600 text-white' :
                'bg-gray-900 text-white'
              }`}>
                {({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram', twitter: 'Twitter' }[content.platform] || content.platform)}
              </span>
            </div>

            {/* Info */}
            <div className="p-3">
              <p className="font-medium text-white truncate text-sm">{content.title || 'Untitled'}</p>
              <p className="text-xs text-white mt-1">{creator?.name || 'Unknown'}</p>
              <div className="flex items-center justify-between mt-2 text-xs text-white">
                <span>{formatViews(content.views)} views</span>
                <span>{new Date(content.postDate).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CampaignContentView;
